import { useEffect, useRef, useState } from 'react'
import { Lock } from 'lucide-react'
import { LuX } from 'react-icons/lu'
import { useHistory } from 'react-router-dom'
import { Button, CodeInput, Modal } from '@cannect/new-components/atoms'
import { TModalActions } from '@cannect/new-components/atoms/Modal'
import {
  TRecipeMaskedPatient,
  TValidateRecipeAccessSuccess,
  useValidateRecipeAccess
} from '@cannect/services/resources/recipeAccess'
import { useRecipeBrandColors } from '../useRecipeBrandColors'

const CODE_LENGTH = 4
const MAX_ATTEMPTS_DISPLAY = 5
// Backoff só de UX — não é a proteção real (rate-limit do servidor, R1 da
// nota [CC][F-receita-codigo-acesso]). Índice = nº de erros consecutivos.
const BACKOFF_SECONDS = [0, 2, 4, 8, 16, 30]

type AccessCodeModalProps = {
  isOpen: boolean
  token: string
  patient: TRecipeMaskedPatient
  onRequestClose: () => void
  onValidated: (data: TValidateRecipeAccessSuccess, reservedWindow?: Window | null) => void
  onTokenExpired: () => void
  reserveNewWindowOnSubmit?: boolean
}

const formatLockedUntil = (iso?: string) => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

// Achado 50 (Lince): ternário aninhado isolado numa função — mesmo
// resultado, sem `no-nested-ternary`. O estado `isPending` já vira spinner
// pelo próprio `Button` (`isLoading`), não precisa de texto aqui.
function getConfirmLabel(backoffSecondsLeft: number) {
  if (backoffSecondsLeft > 0) return `Aguarde ${backoffSecondsLeft}s`
  return 'Confirmar'
}

// D2/B3 do protótipo (`Desktop-B-4-digitos.dc.html`, `B-Receita-4-digitos.dc.html`).
// Decisão 40: o texto usa `patient.phone` REDIGIDO do backend
// (`(**) 9 9832-****`), não o `(11) •••••-••34` do protótipo — aquele
// formato revela 2 dos 4 dígitos que são o próprio código.
export const AccessCodeModal = ({
  isOpen,
  token,
  patient,
  onRequestClose,
  onValidated,
  onTokenExpired,
  reserveNewWindowOnSubmit = false
}: AccessCodeModalProps) => {
  const history = useHistory()
  const colors = useRecipeBrandColors()
  const [code, setCode] = useState('')
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null)
  const [lockedUntil, setLockedUntil] = useState<string | null>(null)
  const [errorCount, setErrorCount] = useState(0)
  const [backoffSecondsLeft, setBackoffSecondsLeft] = useState(0)
  const modalRef = useRef<TModalActions>(null)
  const { mutate, isPending } = useValidateRecipeAccess(token)

  useEffect(() => {
    if (isOpen) modalRef.current?.open()
    else modalRef.current?.close()
  }, [isOpen])

  useEffect(() => {
    if (backoffSecondsLeft <= 0) return undefined
    const timer = setInterval(() => setBackoffSecondsLeft((seconds) => Math.max(0, seconds - 1)), 1000)
    return () => clearInterval(timer)
  }, [backoffSecondsLeft])

  const isLocked = !!lockedUntil

  const resetState = () => {
    setCode('')
    setAttemptsRemaining(null)
    setLockedUntil(null)
    setErrorCount(0)
    setBackoffSecondsLeft(0)
  }

  const handleClose = () => {
    resetState()
    onRequestClose()
  }

  const submit = (submittedCode: string = code) => {
    if (submittedCode.length !== CODE_LENGTH || isLocked || isPending) return

    const reservedWindow = reserveNewWindowOnSubmit ? window.open('about:blank', '_blank') : null

    mutate(
      { last_four_digits: submittedCode },
      {
        onSuccess: (data) => {
          resetState()
          onValidated(data, reservedWindow)
        },
        onError: (error) => {
          reservedWindow?.close()
          setCode('')
          const status = error.response?.status
          const data = error.response?.data

          if (status === 404) {
            onTokenExpired()
            return
          }

          if (status === 429) {
            setLockedUntil(data?.locked_until || new Date(Date.now()).toISOString())
            setAttemptsRemaining(0)
            return
          }

          const nextErrorCount = errorCount + 1
          setErrorCount(nextErrorCount)
          setAttemptsRemaining(typeof data?.attempts_remaining === 'number' ? data.attempts_remaining : null)
          setBackoffSecondsLeft(BACKOFF_SECONDS[Math.min(nextErrorCount, BACKOFF_SECONDS.length - 1)])
        }
      }
    )
  }

  const lockedUntilLabel = formatLockedUntil(lockedUntil || undefined)
  const isConfirmDisabled = isPending || isLocked || backoffSecondsLeft > 0 || code.length !== CODE_LENGTH

  return (
    <Modal
      ref={modalRef}
      hiddenCloseButton
      classNames={{
        containerClassName: 'w-full max-w-full rounded-t-[28px] p-5 sm:max-w-[480px] sm:rounded-3xl sm:p-7'
      }}>
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="mb-1 h-1 w-10 self-center rounded-full bg-gray-100 sm:hidden" />
        <div className="absolute right-4 top-4 sm:right-5 sm:top-5">
          <Button unstyled type="button" aria-label="Fechar" onClick={handleClose}>
            <LuX className="text-gray-800" size={24} />
          </Button>
        </div>

        <div className="flex w-full items-center justify-center gap-3 sm:justify-start">
          <div className={`hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex ${colors.softBg}`}>
            <Lock size={20} className={colors.softText} />
          </div>
          <h2 className="text-xl font-semibold text-gray-950">Confirme para ver a receita</h2>
        </div>

        <p className="text-[15px] leading-relaxed text-gray-500">
          Para proteger seus dados de saúde, digite os 4 últimos números do celular{' '}
          <b className="text-gray-950">{patient.phone}</b>.
        </p>

        <CodeInput
          length={CODE_LENGTH}
          value={code}
          onChange={setCode}
          onComplete={submit}
          ariaLabel="Código de 4 dígitos"
          error={attemptsRemaining !== null}
          disabled={isPending || isLocked}
          autoFocus
        />

        {isLocked && (
          <p className="-mt-1 text-sm text-red-600">
            Muitas tentativas incorretas. Tente novamente{' '}
            {lockedUntilLabel ? `às ${lockedUntilLabel}` : 'em 10 minutos'}.
          </p>
        )}

        {!isLocked && attemptsRemaining !== null && (
          <p className="-mt-1 text-sm text-red-600">
            Código incorreto. {attemptsRemaining} de {MAX_ATTEMPTS_DISPLAY} tentativas.
          </p>
        )}

        <Button
          full
          isLoading={isPending}
          disabled={isConfirmDisabled}
          onClick={() => submit()}
          className={`h-[54px] rounded-[110px] text-base ${colors.cta}`}>
          {getConfirmLabel(backoffSecondsLeft)}
        </Button>

        <Button
          unstyled
          type="button"
          onClick={() => history.push(`/receitas/${token}/ajuda`)}
          className={`text-sm font-semibold ${colors.link}`}>
          Não reconhece esse número? Fale com a equipe
        </Button>
      </div>
    </Modal>
  )
}
